// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type {
  Appointment,
  DeviceRequest,
  DiagnosticReport,
  Encounter,
  Extension,
  GuidanceResponse,
  MedicationRequest,
  Practitioner,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  IconClipboardList,
  IconFilePlus,
  IconFlask,
  IconHeartbeat,
  IconPlus,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { NewPrescriptionModal } from '../../components/encounter/NewPrescriptionModal';
import { OrderNewTestModal } from '../../components/encounter/OrderNewTestModal';
import { ReferralModal } from '../../components/encounter/ReferralModal';
import { usePatient } from '../../hooks/usePatient';
import { useRole } from '../../hooks/useRole';
import { getBinaryDownloadUrl } from '../../utils/binary';
import { downloadOrderClassTemplate } from '../../utils/order-download-pdf';
import { hasPermission } from '../../utils/permissions';
import {
  downloadGuidanceResponseReferralPdf,
  isReferralMedicationRequest,
  viewServiceRequestReferralPdf,
} from '../../utils/referral-pdf';
import { withTenantScope } from '../../utils/tenant';

type OrderCategory = 'dme' | 'test' | 'rpm' | 'referral' | 'rx';
type FilterCategory = 'all' | OrderCategory;
type OrderTone = 'ok' | 'wait' | 'done';

interface OrderMeta {
  label: string;
  value: string;
}

interface OrderCardData {
  id: string;
  category: OrderCategory;
  title: string;
  orderId: string;
  party: string;
  status?: string;
  tone: OrderTone;
  date?: string;
  by?: string;
  meta: OrderMeta[];
  searchable: string;
  resourceType: 'ServiceRequest' | 'MedicationRequest' | 'DeviceRequest' | 'GuidanceResponse';
  resourceId?: string;
  mutable: boolean;
}

interface NewOrderModalProps {
  opened: boolean;
  canHsat: boolean;
  canRpm: boolean;
  canReferral: boolean;
  onSelect: (type: OrderCategory) => void;
  onClose: () => void;
}

const CATEGORY_INFO: Record<OrderCategory, { label: string; plural: string }> = {
  dme: { label: 'DME', plural: 'DME & supplies' },
  test: { label: 'Tests', plural: 'Tests & studies' },
  rpm: { label: 'RPM', plural: 'Remote patient monitoring' },
  referral: { label: 'Referrals', plural: 'Referrals' },
  rx: { label: 'Prescriptions', plural: 'Prescriptions' },
};
const CATEGORY_ORDER: OrderCategory[] = ['dme', 'test', 'rpm', 'referral', 'rx'];
const THERAPY_SYSTEM = 'http://polarissleep.com/therapies';
const RPM_ORDER_SYSTEM = 'https://polarissleep.com/fhir/prescription-type';
const HSAT_PATTERN = /home sleep apnea test|hsat|watchpat/i;
const DME_STATUS_EXTENSION = 'https://polarissleep.com/fhir/StructureDefinition/dme-order-status';
const CATEGORY_CHIP_STYLES: Record<OrderCategory, { backgroundColor: string; color: string }> = {
  dme: { backgroundColor: '#f6e3ed', color: '#640040' },
  test: { backgroundColor: '#d8f5c5', color: '#296b36' },
  rpm: { backgroundColor: '#ebf0e8', color: '#405044' },
  referral: { backgroundColor: '#fefea5', color: '#695f00' },
  rx: { backgroundColor: '#eaf8e9', color: '#2d6a3a' },
};

function formatDate(value: string | undefined): string | undefined {
  return value ? dayjs(value).format('MMM D, YYYY') : undefined;
}

function formatStatus(value: string | undefined): { label?: string; tone: OrderTone } {
  if (!value || value === 'unknown' || value === 'entered-in-error') {
    return { tone: 'wait' };
  }
  const label =
    value === 'active' ? 'Open' : value.replaceAll('-', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  const tone: OrderTone = ['completed', 'resulted', 'final'].includes(value) ? 'done' : 'ok';
  return { label, tone };
}

function codeValues(resource: ServiceRequest | MedicationRequest | DeviceRequest): string[] {
  let concept;
  if (resource.resourceType === 'ServiceRequest') {
    concept = resource.code;
  } else if (resource.resourceType === 'MedicationRequest') {
    concept = resource.medicationCodeableConcept;
  } else {
    concept = resource.codeCodeableConcept;
  }
  return [concept?.text, ...(concept?.coding ?? []).map((coding) => coding.display ?? coding.code)].filter(
    (value): value is string => Boolean(value)
  );
}

function resourceDate(resource: ServiceRequest | MedicationRequest | DeviceRequest): string | undefined {
  if ('authoredOn' in resource && resource.authoredOn) {
    return resource.authoredOn;
  }
  if ('occurrenceDateTime' in resource && resource.occurrenceDateTime) {
    return resource.occurrenceDateTime;
  }
  return resource.resourceType === 'ServiceRequest' ? resource.occurrencePeriod?.start : undefined;
}

function referenceDisplay(reference: { display?: string; reference?: string } | undefined): string | undefined {
  return reference?.display ?? reference?.reference;
}

function sourceLabel(resource: { encounter?: { display?: string; reference?: string } }): string {
  return referenceDisplay(resource.encounter) ?? 'Patient chart';
}

function orderId(resource: { id?: string; identifier?: { value?: string }[] }, prefix: string): string {
  return resource.identifier?.find((identifier) => identifier.value)?.value ?? `${prefix}-${resource.id ?? 'pending'}`;
}

function serviceCategory(request: ServiceRequest): OrderCategory {
  const text = [request.code?.text, ...(request.code?.coding ?? []).map((coding) => coding.display)]
    .join(' ')
    .toLowerCase();
  return text.includes('referral') ? 'referral' : 'test';
}

function medicationCategory(request: MedicationRequest): OrderCategory {
  if (isReferralMedicationRequest(request)) {
    return 'referral';
  }
  const codings = request.medicationCodeableConcept?.coding ?? [];
  if (codings.some((coding) => coding.system === RPM_ORDER_SYSTEM)) {
    return 'rpm';
  }
  if (codings.some((coding) => coding.system === THERAPY_SYSTEM)) {
    return 'dme';
  }
  return 'rx';
}

function isMutable(category: OrderCategory, party: string): boolean {
  return category !== 'referral' && (category === 'rpm' || !/watchpat|parachute/i.test(party));
}

function isCancelledResource(resource: ServiceRequest | MedicationRequest | DeviceRequest | GuidanceResponse): boolean {
  if (resource.resourceType === 'GuidanceResponse') {
    return resource.status === 'failure' || resource.status === 'entered-in-error';
  }
  return resource.status === 'cancelled' || resource.status === 'revoked' || resource.status === 'entered-in-error';
}

function matchesHsatStatus(order: OrderCardData, status: string): boolean {
  if (status === 'all') {
    return true;
  }
  return (
    order.category === 'test' &&
    Boolean(order.status) &&
    (status === 'open' ? order.tone !== 'done' : order.tone === 'done')
  );
}

function metadata(
  values: [string, string | undefined][],
  date: string | undefined,
  by: string | undefined,
  source: string
): OrderMeta[] {
  const entries = [
    ...values,
    ['Ordered', [formatDate(date), by].filter(Boolean).join(' · ') || undefined],
    ['Placed from', source],
  ];
  return entries
    .filter((entry) => Boolean(entry[1]))
    .map(([label, value]) => ({ label: label ?? '', value: value as string }));
}

function makeServiceCard(request: ServiceRequest): OrderCardData {
  const category = serviceCategory(request);
  const date = resourceDate(request);
  const by = request.requester?.display;
  const title = codeValues(request)[0] ?? (category === 'referral' ? 'Referral' : 'Test order');
  const status = formatStatus(request.status);
  const indication = request.reasonCode
    ?.map((reason) => reason.text ?? reason.coding?.[0]?.display ?? reason.coding?.[0]?.code)
    .filter(Boolean)
    .join(', ');
  const meta = metadata(
    category === 'test'
      ? [
          ['Indication', indication],
          ['CPT', request.code?.coding?.find((coding) => coding.system?.includes('cpt'))?.code],
          ['Result', undefined],
        ]
      : [
          ['Reason', indication],
          ['Recipient', request.performer?.[0]?.display],
          ['Sent', formatDate(date)],
        ],
    date,
    by,
    sourceLabel(request)
  );
  const party = request.performer?.[0]?.display ?? request.code?.coding?.[0]?.display ?? 'Polaris Sleep';
  return {
    id: `service-${request.id}`,
    category,
    title,
    orderId: orderId(request, category === 'test' ? 'TST' : 'REF'),
    party,
    status: HSAT_PATTERN.test(title) ? status.label : undefined,
    tone: status.tone,
    date,
    by,
    meta,
    searchable: [
      title,
      orderId(request, 'ORD'),
      request.status,
      by,
      ...meta.flatMap(({ label, value }) => [label, value]),
    ]
      .filter(Boolean)
      .join(' '),
    resourceType: 'ServiceRequest',
    resourceId: request.id,
    mutable:
      isMutable(category, party) &&
      !isCancelledResource(request) &&
      !(HSAT_PATTERN.test(title) && status.tone === 'done'),
  };
}

export function makeMedicationCard(request: MedicationRequest): OrderCardData {
  const category = medicationCategory(request);
  const date = resourceDate(request);
  const by = request.requester?.display;
  const fallbackTitle = {
    dme: 'Prescription',
    referral: 'Referral',
    rpm: 'Remote Patient Monitoring',
    rx: 'Prescription',
    test: 'Prescription',
  }[category];
  const title = codeValues(request)[0] ?? fallbackTitle;
  const status = formatStatus(request.status);
  const notes =
    request.note
      ?.map((note) => note.text)
      .filter(Boolean)
      .join('\n') ?? '';
  const indication = notes.match(/(?:Diagnosis|Indication): ([^\n]+)/)?.[1];
  const referralTarget = notes.match(/Referral target: ([^\n]+)/)?.[1];
  let metaValues: [string, string | undefined][];
  if (category === 'referral') {
    // ReferralModal writes `Reason: …` to the note and the diagnosis to reasonCode; older orders used `Diagnosis: …`.
    const referralDiagnosis = request.reasonCode
      ?.map((reason) => reason.coding?.[0]?.display ?? reason.text ?? reason.coding?.[0]?.code)
      .filter(Boolean)
      .join(', ');
    metaValues = [
      ['Reason', notes.match(/(?:^|\n)Reason: ([^\n]+)/)?.[1] ?? indication],
      ['Diagnosis', referralDiagnosis || undefined],
      ['Refer to', referralTarget],
    ];
  } else if (category === 'dme') {
    metaValues = [
      ['Items', title],
      [
        'HCPCS',
        request.medicationCodeableConcept?.coding
          ?.map((coding) => coding.code)
          .filter(Boolean)
          .join(', '),
      ],
    ];
  } else if (category === 'rpm') {
    metaValues = [
      ['Diagnosis', indication],
      ['Device', notes.match(/PAP Device: ([^\n]+)/)?.[1]],
    ];
  } else {
    metaValues = [
      ['Sig', request.dosageInstruction?.[0]?.text],
      [
        'Quantity',
        request.dispenseRequest?.quantity
          ? `${request.dispenseRequest.quantity.value ?? ''} ${request.dispenseRequest.quantity.unit ?? ''}`.trim()
          : undefined,
      ],
      ['Indication', indication],
    ];
  }
  const meta = metadata(metaValues, date, by, sourceLabel(request));
  let prefix = 'RX';
  if (category === 'referral') {
    prefix = 'REF';
  } else if (category === 'rpm') {
    prefix = 'RPM';
  } else if (category === 'dme') {
    prefix = 'DME';
  }
  let party = request.dispenseRequest?.performer?.display ?? 'Pharmacy / DME';
  if (category === 'rpm') {
    party = 'Polaris Sleep';
  } else if (category === 'referral') {
    party = referralTarget ?? 'Polaris Sleep';
  }
  return {
    id: `medication-${request.id}`,
    category,
    title,
    orderId: orderId(request, prefix),
    party,
    tone: status.tone,
    date,
    by,
    meta,
    searchable: [
      title,
      orderId(request, 'ORD'),
      request.status,
      by,
      notes,
      ...meta.flatMap(({ label, value }) => [label, value]),
    ]
      .filter(Boolean)
      .join(' '),
    resourceType: 'MedicationRequest',
    resourceId: request.id,
    mutable: isMutable(category, party) && !isCancelledResource(request),
  };
}

function makeDeviceCard(request: DeviceRequest): OrderCardData {
  const date = request.authoredOn;
  const by = request.requester?.display;
  const title = codeValues(request)[0] ?? 'DME / supplies';
  const status = formatStatus(request.status);
  const meta = metadata(
    [
      ['Items', title],
      [
        'HCPCS',
        request.codeCodeableConcept?.coding
          ?.map((coding) => coding.code)
          .filter(Boolean)
          .join(', '),
      ],
    ],
    date,
    by,
    sourceLabel(request)
  );
  const party = request.performer?.display ?? 'DME supplier';
  return {
    id: `device-${request.id}`,
    category: 'dme',
    title,
    orderId: orderId(request, 'DME'),
    party,
    tone: status.tone,
    date,
    by,
    meta,
    searchable: [
      title,
      orderId(request, 'DME'),
      request.status,
      by,
      ...meta.flatMap(({ label, value }) => [label, value]),
    ]
      .filter(Boolean)
      .join(' '),
    resourceType: 'DeviceRequest',
    resourceId: request.id,
    mutable: isMutable('dme', party) && !isCancelledResource(request),
  };
}

function makeReferralCard(response: GuidanceResponse): OrderCardData {
  const date = response.occurrenceDateTime;
  const performer = response.extension?.find((extension) => extension.url?.includes('performer'))?.valueReference
    ?.display;
  const meta = metadata(
    [
      [
        'Reason',
        response.note
          ?.map((note) => note.text)
          .filter(Boolean)
          .join(' '),
      ],
      ['Sent', formatDate(date)],
      ['Recipient', performer],
    ],
    date,
    undefined,
    'Patient chart'
  );
  const party = performer ?? 'Care management to select';
  return {
    id: `referral-${response.id}`,
    category: 'referral',
    title: response.moduleUri ?? 'Referral',
    orderId: orderId(response, 'REF'),
    party,
    tone: 'wait',
    date,
    meta,
    searchable: [response.moduleUri, response.status, ...meta.flatMap(({ label, value }) => [label, value])]
      .filter(Boolean)
      .join(' '),
    resourceType: 'GuidanceResponse',
    resourceId: response.id,
    mutable: isMutable('referral', party) && !isCancelledResource(response),
  };
}

function enrichTestCard(card: OrderCardData, reports: DiagnosticReport[], appointments: Appointment[]): OrderCardData {
  if (card.category !== 'test' || !card.resourceId) {
    return card;
  }
  const reference = `ServiceRequest/${card.resourceId}`;
  const report = reports.find((candidate) => candidate.basedOn?.some((item) => item.reference === reference));
  const appointment = appointments.find((candidate) => candidate.basedOn?.some((item) => item.reference === reference));
  const metadataLabels = new Set(card.meta.map((item) => item.label));
  const additions = [
    !metadataLabels.has('Result') && report
      ? { label: 'Result', value: report.conclusion ?? report.status }
      : undefined,
    !metadataLabels.has('Appointment') && appointment?.start
      ? { label: 'Appointment', value: formatDate(appointment.start) }
      : undefined,
  ].filter((item): item is OrderMeta => Boolean(item?.value));
  return additions.length
    ? {
        ...card,
        meta: [...card.meta, ...additions],
        searchable: `${card.searchable} ${additions.map((item) => `${item.label} ${item.value}`).join(' ')}`,
      }
    : card;
}

export function NewOrderModal({
  opened,
  canHsat,
  canRpm,
  canReferral,
  onSelect,
  onClose,
}: NewOrderModalProps): JSX.Element {
  const options = [
    {
      label: 'DME',
      title: 'DME / therapy order',
      description: 'PAP device, mask, supplies, or settings change.',
      icon: <IconHeartbeat size={20} />,
      disabled: false,
      type: 'dme' as const,
    },
    {
      label: 'Tests',
      title: 'Test order',
      description: 'Home sleep apnea test or sleep study.',
      icon: <IconFlask size={20} />,
      disabled: !canHsat,
      type: 'test' as const,
    },
    {
      label: 'Referrals',
      title: 'Referral',
      description: 'Send patient to outside clinic or specialist.',
      icon: <IconRefresh size={20} />,
      disabled: !canReferral,
      type: 'referral' as const,
    },
    {
      label: 'Prescriptions',
      title: 'Prescription',
      description: "Medication order routed to patient's pharmacy.",
      icon: <IconFilePlus size={20} />,
      disabled: false,
      type: 'rx' as const,
    },
    {
      label: 'RPM',
      title: 'Remote patient monitoring',
      description: 'Enroll patient in RPM monitoring.',
      icon: <IconClipboardList size={20} />,
      disabled: !canRpm,
      type: 'rpm' as const,
    },
  ];
  return (
    <Modal opened={opened} onClose={onClose} title="New order" centered size="lg">
      <Text size="sm" c="dimmed" mb="md">
        Choose order type for this patient. Patient context is already set.
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {options.map((option) => (
          <UnstyledButton key={option.label} disabled={option.disabled} onClick={() => onSelect(option.type)}>
            <Card withBorder radius="md" padding="md" h="100%" style={{ opacity: option.disabled ? 0.55 : 1 }}>
              <Group wrap="nowrap" align="flex-start">
                {option.icon}
                <div>
                  <Text fw={600}>{option.title}</Text>
                  <Text size="sm" c="dimmed" mt={4}>
                    {option.description}
                  </Text>
                  {option.disabled && (
                    <Text size="xs" c="dimmed" mt="sm">
                      Not available for this role
                    </Text>
                  )}
                </div>
              </Group>
            </Card>
          </UnstyledButton>
        ))}
      </SimpleGrid>
    </Modal>
  );
}

interface OrderCardProps {
  order: OrderCardData;
  onEdit: (order: OrderCardData) => void;
  onCancel: (order: OrderCardData) => void;
  onDownload: (order: OrderCardData) => void;
}

function OrderCard({ order, onEdit, onCancel, onDownload }: OrderCardProps): JSX.Element {
  let orderAction: JSX.Element;
  if (order.mutable) {
    orderAction = (
      <Button variant="subtle" size="compact-sm" onClick={() => onEdit(order)}>
        Edit
      </Button>
    );
  } else {
    orderAction = (
      <Text size="sm" c="dimmed" title={`Sent to ${order.party} — change it there`}>
        Sent
      </Text>
    );
  }

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={700}>{order.title}</Text>
          {order.status && (
            <Badge color={order.tone === 'done' ? 'green' : 'blue'} variant="light">
              {order.status}
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          <Badge size="sm" radius="sm" style={CATEGORY_CHIP_STYLES[order.category]}>
            {CATEGORY_INFO[order.category].label}
          </Badge>
          <Text size="sm" c="dimmed">
            {order.orderId} · {order.party}
          </Text>
        </Group>
        <Box component="dl" m={0} style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 14px' }}>
          {order.meta.map((item) => (
            <Box component="div" key={`${order.id}-${item.label}`} style={{ display: 'contents' }}>
              <Text component="dt" size="xs" fw={700} c="dimmed" tt="uppercase">
                {item.label}
              </Text>
              <Text component="dd" size="sm" m={0}>
                {item.value}
              </Text>
            </Box>
          ))}
        </Box>
        <Group gap="xs">
          {orderAction}
          <Button variant="subtle" size="compact-sm" onClick={() => onDownload(order)}>
            Download
          </Button>
          {order.mutable && (
            <Button variant="subtle" color="red" size="compact-sm" onClick={() => onCancel(order)}>
              Cancel order
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

export function OrdersTab(): JSX.Element {
  const patient = usePatient();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role, isAdmin } = useRole();
  const [orders, setOrders] = useState<OrderCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<FilterCategory>('all');
  const [hsatStatus, setHsatStatus] = useState('all');
  const [orderedBy, setOrderedBy] = useState('all');
  const [sort, setSort] = useState('newest');
  const [newOrderOpened, setNewOrderOpened] = useState(false);
  const [newOrderType, setNewOrderType] = useState<OrderCategory>();
  const [orderEncounter, setOrderEncounter] = useState<Encounter>();
  const [editingTestOrder, setEditingTestOrder] = useState<ServiceRequest>();
  const [editingDmeOrder, setEditingDmeOrder] = useState<MedicationRequest>();
  const [cancelTarget, setCancelTarget] = useState<OrderCardData>();
  const [cancelReason, setCancelReason] = useState('');
  const handledActionRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!patient?.id) {
      return;
    }
    let disposed = false;
    setLoading(true);
    Promise.all([
      medplum.searchResources('ServiceRequest', { subject: `Patient/${patient.id}`, _count: '100' }).catch(() => []),
      medplum.searchResources('MedicationRequest', { subject: `Patient/${patient.id}`, _count: '100' }).catch(() => []),
      medplum.searchResources('DeviceRequest', { subject: `Patient/${patient.id}`, _count: '100' }).catch(() => []),
      medplum.searchResources('GuidanceResponse', { subject: `Patient/${patient.id}`, _count: '100' }).catch(() => []),
      medplum.searchResources('DiagnosticReport', { subject: `Patient/${patient.id}`, _count: '100' }).catch(() => []),
      medplum.searchResources('Appointment', { patient: `Patient/${patient.id}`, _count: '100' }).catch(() => []),
    ])
      .then(([services, medications, devices, referrals, reports, appointments]) => {
        if (disposed) {
          return;
        }
        const reportResources = reports as DiagnosticReport[];
        const appointmentResources = appointments as Appointment[];
        setOrders(
          [
            ...(services as ServiceRequest[]).map(makeServiceCard),
            ...(medications as MedicationRequest[]).map(makeMedicationCard),
            ...(devices as DeviceRequest[]).map(makeDeviceCard),
            ...(referrals as GuidanceResponse[]).map(makeReferralCard),
          ].map((order) => enrichTestCard(order, reportResources, appointmentResources))
        );
      })
      .finally(() => {
        if (!disposed) {
          setLoading(false);
        }
      });
    return () => {
      disposed = true;
    };
  }, [medplum, patient?.id]);

  const providers = useMemo(
    () => [...new Set(orders.map((order) => order.by).filter((value): value is string => Boolean(value)))].sort(),
    [orders]
  );
  const patientSearchText = [
    patient?.name?.[0]?.text,
    ...(patient?.name?.[0]?.given ?? []),
    patient?.name?.[0]?.family,
    ...(patient?.identifier ?? []).map((identifier) => identifier.value),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const countPool = useMemo(() => {
    const value = query.trim().toLowerCase();
    return orders.filter((order) => {
      const searchable = `${order.searchable} ${patientSearchText}`.toLowerCase();
      return (
        (!value || searchable.includes(value)) &&
        (orderedBy === 'all' || order.by === orderedBy) &&
        matchesHsatStatus(order, hsatStatus)
      );
    });
  }, [hsatStatus, orderedBy, orders, patientSearchText, query]);
  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    const visible = orders.filter((order) => {
      const statusMatches = matchesHsatStatus(order, hsatStatus);
      return (
        (category === 'all' || order.category === category) &&
        (!value || `${order.searchable} ${patientSearchText}`.toLowerCase().includes(value)) &&
        (orderedBy === 'all' || order.by === orderedBy) &&
        statusMatches
      );
    });
    return visible.sort((a, b) => {
      if (sort === 'name') {
        return a.title.localeCompare(b.title);
      }
      if (sort === 'oldest') {
        return (a.date ?? '').localeCompare(b.date ?? '');
      }
      if (sort === 'status') {
        return Number(a.tone === 'wait') - Number(b.tone === 'wait') || (b.date ?? '').localeCompare(a.date ?? '');
      }
      return (b.date ?? '').localeCompare(a.date ?? '');
    });
  }, [category, hsatStatus, orders, orderedBy, patientSearchText, query, sort]);

  const resetFilters = (): void => {
    setQuery('');
    setCategory('all');
    setHsatStatus('all');
    setOrderedBy('all');
    setSort('newest');
  };

  const startNewOrder = useCallback(
    async (type: OrderCategory): Promise<void> => {
      setNewOrderOpened(false);
      if (type === 'rpm') {
        navigate(`/RpmOrder/${patient?.id}`);
        return;
      }
      if (type === 'rx') {
        navigate(`/Patient/${patient?.id}/prescribe`);
        return;
      }
      // DME and referral orders from the chart are not tied to an encounter; only test orders need one.
      if (type === 'dme' || type === 'referral') {
        setNewOrderType(type);
        return;
      }
      if (!patient?.id) {
        return;
      }
      const encounters = await medplum.searchResources('Encounter', {
        subject: `Patient/${patient.id}`,
        _count: '100',
      });
      const latest = encounters
        .filter((encounter): encounter is Encounter & { id: string } => Boolean(encounter.id))
        .sort((a, b) => (b.period?.start ?? '').localeCompare(a.period?.start ?? ''))[0];
      const encounter =
        latest ??
        (await medplum.createResource<Encounter>({
          resourceType: 'Encounter',
          status: 'in-progress',
          class: {
            system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
            code: 'AMB',
            display: 'ambulatory',
          },
          subject: { reference: `Patient/${patient.id}` },
          period: { start: new Date().toISOString() },
        }));
      setOrderEncounter(encounter);
      setNewOrderType(type);
    },
    [medplum, navigate, patient?.id]
  );

  const editOrder = useCallback(
    async (order: OrderCardData): Promise<void> => {
      if (!order.resourceId || !patient?.id) {
        return;
      }
      if (order.category === 'rpm') {
        navigate(`/RpmOrder/${patient.id}?prescriptionId=${order.resourceId}`, { replace: true });
      } else if (order.category === 'dme') {
        // Always the server's latest version: amending a cached copy would revert a newer amendment.
        const medicationRequest = await medplum.readResource('MedicationRequest', order.resourceId, {
          cache: 'no-cache',
        });
        setEditingDmeOrder(medicationRequest);
        setNewOrderType('dme');
      } else if (order.category === 'test') {
        const serviceRequest = await medplum.readResource('ServiceRequest', order.resourceId);
        let encounter = serviceRequest.encounter?.reference
          ? await medplum.readReference<Encounter>(serviceRequest.encounter)
          : undefined;
        if (!encounter) {
          const encounters = await medplum.searchResources('Encounter', {
            subject: `Patient/${patient.id}`,
            _count: '100',
          });
          encounter = encounters
            .filter((candidate): candidate is Encounter & { id: string } => Boolean(candidate.id))
            .sort((a, b) => (b.period?.start ?? '').localeCompare(a.period?.start ?? ''))[0];
        }
        if (!encounter) {
          encounter = await medplum.createResource<Encounter>({
            resourceType: 'Encounter',
            status: 'in-progress',
            class: {
              system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
              code: 'AMB',
              display: 'ambulatory',
            },
            subject: { reference: `Patient/${patient.id}` },
            period: { start: new Date().toISOString() },
          });
        }
        setOrderEncounter(encounter);
        setEditingTestOrder(serviceRequest);
        setNewOrderType('test');
      } else {
        navigate(`/Patient/${patient.id}/prescribe?prescriptionId=${order.resourceId}`);
      }
    },
    [medplum, navigate, patient?.id]
  );

  const downloadOrder = useCallback(
    async (order: OrderCardData): Promise<void> => {
      if (!order.resourceId || !patient?.id) {
        return;
      }
      try {
        if (order.resourceType === 'GuidanceResponse') {
          const response = await medplum.readResource('GuidanceResponse', order.resourceId);
          const practitioner = medplum.getProfile();
          if (practitioner?.resourceType !== 'Practitioner') {
            throw new Error('Signing provider profile is unavailable.');
          }
          await downloadGuidanceResponseReferralPdf(medplum, patient, response, practitioner);
          return;
        }
        if (order.category === 'referral' && order.resourceType === 'ServiceRequest') {
          const serviceRequest = await medplum.readResource('ServiceRequest', order.resourceId);
          const practitioner = medplum.getProfile();
          if (practitioner?.resourceType !== 'Practitioner') {
            throw new Error('Signing provider profile is unavailable.');
          }
          const documents = await medplum.searchResources('DocumentReference', {
            subject: `Patient/${patient.id}`,
            _count: '100',
          });
          const document = documents.find((candidate) =>
            candidate.context?.related?.some(
              (related: Reference) => related.reference === `ServiceRequest/${order.resourceId}`
            )
          );
          if (!document?.content?.[0]?.attachment?.url) {
            await viewServiceRequestReferralPdf(medplum, patient, serviceRequest, practitioner);
            return;
          }
        }
        const documents = await medplum.searchResources('DocumentReference', {
          subject: `Patient/${patient.id}`,
          _count: '100',
        });
        const reference = `${order.resourceType}/${order.resourceId}`;
        const document = documents.find((candidate) =>
          candidate.context?.related?.some((related: Reference) => related.reference === reference)
        );
        const url = document?.content?.[0]?.attachment?.url;
        if (!url) {
          showNotification({ color: 'blue', message: `Generating PDF — ${order.orderId}.` });
          await downloadOrderClassTemplate({
            category: CATEGORY_INFO[order.category].label,
            title: order.title,
            orderId: order.orderId,
            patientName: patient.name?.[0]?.text ?? patient.id,
            fields: order.meta,
          });
          return;
        }
        showNotification({ color: 'blue', message: `Opening PDF — ${order.orderId}.` });
        const href = url.startsWith('Binary/')
          ? URL.createObjectURL(await medplum.download(getBinaryDownloadUrl(url) as string))
          : url;
        window.open(href, '_blank', 'noopener,noreferrer');
      } catch (error) {
        showNotification({ color: 'red', message: normalizeErrorString(error) });
      }
    },
    [medplum, patient]
  );

  useEffect(() => {
    const requestedNewOrder = searchParams.get('newOrder') as OrderCategory | null;
    const resourceId = searchParams.get('orderId');
    const action = searchParams.get('action');
    if (requestedNewOrder && ['dme', 'test', 'rpm', 'referral', 'rx'].includes(requestedNewOrder)) {
      const key = `new:${requestedNewOrder}`;
      if (handledActionRef.current !== key) {
        handledActionRef.current = key;
        startNewOrder(requestedNewOrder).catch((error) =>
          showNotification({ color: 'red', message: normalizeErrorString(error) })
        );
      }
      return;
    }
    if (!resourceId || !action || handledActionRef.current === `${action}:${resourceId}`) {
      return;
    }
    const target = orders.find((order) => order.resourceId === resourceId);
    if (!target) {
      return;
    }
    handledActionRef.current = `${action}:${resourceId}`;
    if (action === 'cancel') {
      setCancelTarget(target);
    } else if (action === 'edit') {
      setSearchParams({}, { replace: true });
      editOrder(target).catch((error) => showNotification({ color: 'red', message: normalizeErrorString(error) }));
    } else if (action === 'download') {
      setSearchParams({}, { replace: true });
      downloadOrder(target).catch((error) => showNotification({ color: 'red', message: normalizeErrorString(error) }));
    }
  }, [downloadOrder, editOrder, orders, searchParams, setSearchParams, startNewOrder]);

  const cancelOrder = async (): Promise<void> => {
    if (!cancelTarget?.resourceId || !patient?.id || !cancelReason.trim()) {
      return;
    }
    try {
      const resource = await medplum.readResource(cancelTarget.resourceType, cancelTarget.resourceId);
      let status = 'cancelled';
      if (cancelTarget.resourceType === 'GuidanceResponse') {
        status = 'failure';
      } else if (cancelTarget.resourceType === 'ServiceRequest') {
        status = 'revoked';
      }
      const cancellationExtension =
        cancelTarget.category === 'dme'
          ? [
              ...(resource.extension ?? []).filter((extension: Extension) => extension.url !== DME_STATUS_EXTENSION),
              { url: DME_STATUS_EXTENSION, valueCode: 'cancelled' },
            ]
          : resource.extension;
      await medplum.updateResource({
        ...resource,
        status: status as never,
        extension: cancellationExtension,
        note: [...(resource.note ?? []), { text: `Cancelled: ${cancelReason.trim()}` }],
      });
      const relatedTasks = await medplum.searchResources('Task', {
        patient: `Patient/${patient.id}`,
        _count: '100',
      });
      const orderReference = `${cancelTarget.resourceType}/${cancelTarget.resourceId}`;
      const relatedTask = relatedTasks.find(
        (task) => task.status !== 'cancelled' && task.status !== 'completed' && task.focus?.reference === orderReference
      );
      await Promise.all(
        relatedTasks
          .filter(
            (task) =>
              task.status !== 'cancelled' &&
              task.status !== 'completed' &&
              task.status !== 'failed' &&
              task.status !== 'rejected' &&
              task.focus?.reference === orderReference
          )
          .map((task) =>
            medplum.updateResource({
              ...task,
              status: 'cancelled',
              statusReason: { text: `Order cancelled: ${cancelReason.trim()}` },
            })
          )
      );
      await medplum.createResource(
        withTenantScope({
          resourceType: 'Task',
          status: 'requested',
          intent: 'order',
          owner: relatedTask?.owner,
          description: `Order ${cancelTarget.orderId} cancelled: ${cancelReason.trim()}`,
          for: { reference: `Patient/${patient.id}` },
        })
      );
      setOrders((current) => current.filter((order) => order.id !== cancelTarget.id));
      setCancelTarget(undefined);
      setCancelReason('');
      showNotification({ color: 'green', message: 'Order cancelled.' });
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error) });
    }
  };

  if (!patient?.id) {
    return <Loader />;
  }
  const canHsat = hasPermission({ role, permission: 'place-hsat-order', isAdmin });
  const canRpm = hasPermission({ role, permission: 'prescribe-cpap', isAdmin });
  // UX gate only; the AccessPolicy is the real boundary (care coordinators may write referral MedicationRequests only).
  const canReferral = hasPermission({ role, permission: 'create-referral-order', isAdmin });
  let results: JSX.Element;
  if (loading) {
    results = (
      <Box ta="center" py="xl">
        <Loader size="sm" />
      </Box>
    );
  } else if (filtered.length === 0) {
    results = (
      <Card withBorder radius="md" padding="xl" ta="center" bg="gray.0">
        <Text fw={600}>{orders.length ? 'No orders match these filters.' : 'No orders on this chart yet.'}</Text>
        {orders.length > 0 && (
          <Button variant="subtle" mt="sm" onClick={resetFilters}>
            Clear filters
          </Button>
        )}
      </Card>
    );
  } else {
    results = (
      <Stack gap="xl">
        {(category === 'all' ? CATEGORY_ORDER : [category]).map((key) => {
          const group = filtered.filter((order) => order.category === key);
          if (!group.length) {
            return null;
          }
          return (
            <section key={key} aria-labelledby={`orders-${key}`}>
              <Group justify="space-between" mb="sm">
                <Title order={3} id={`orders-${key}`}>
                  {CATEGORY_INFO[key].plural}
                </Title>
                <Text size="sm" c="dimmed">
                  {group.length}
                </Text>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                {group.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onEdit={editOrder}
                    onCancel={(target) => {
                      setCancelTarget(target);
                      setCancelReason('');
                    }}
                    onDownload={(target) => {
                      downloadOrder(target).catch(console.error);
                    }}
                  />
                ))}
              </SimpleGrid>
            </section>
          );
        })}
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="md" style={{ minWidth: 0 }}>
      <Paper withBorder radius="md" p="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" style={{ minWidth: 0 }}>
          <div style={{ minWidth: 0, flex: '1 1 320px' }}>
            <Title order={2}>Orders</Title>
            <Text c="dimmed" size="sm" mt={4}>
              Every order on this chart — DME, tests, referrals and prescriptions — in one place.
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setNewOrderOpened(true)}>
            New order
          </Button>
        </Group>
      </Paper>
      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <Group gap="xs" wrap="wrap" role="tablist" aria-label="Order type">
            <Button
              role="tab"
              aria-selected={category === 'all'}
              variant={category === 'all' ? 'filled' : 'subtle'}
              size="compact-sm"
              onClick={() => setCategory('all')}
            >
              All orders{' '}
              <Badge size="sm" ml={4}>
                {countPool.length}
              </Badge>
            </Button>
            {CATEGORY_ORDER.map((key) => (
              <Button
                key={key}
                role="tab"
                aria-selected={category === key}
                variant={category === key ? 'filled' : 'subtle'}
                size="compact-sm"
                onClick={() => setCategory(key)}
              >
                {CATEGORY_INFO[key].label}{' '}
                <Badge size="sm" ml={4}>
                  {countPool.filter((order) => order.category === key).length}
                </Badge>
              </Button>
            ))}
          </Group>
          <Group align="flex-end" wrap="wrap" style={{ minWidth: 0 }}>
            <TextInput
              style={{ flex: '1 1 260px', minWidth: 180 }}
              placeholder="Search orders, IDs, suppliers, codes"
              aria-label="Search orders"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
            />
            <Select
              label="HSAT status"
              data={[
                { value: 'all', label: 'All' },
                { value: 'open', label: 'Open' },
                { value: 'closed', label: 'Resulted' },
              ]}
              value={hsatStatus}
              onChange={(value) => setHsatStatus(value ?? 'all')}
              w={150}
            />
            <Select
              label="Ordered by"
              data={[
                { value: 'all', label: 'Anyone' },
                ...providers.map((provider) => ({ value: provider, label: provider })),
              ]}
              value={orderedBy}
              onChange={(value) => setOrderedBy(value ?? 'all')}
              w={190}
            />
            <Select
              label="Sort"
              data={[
                { value: 'newest', label: 'Newest' },
                { value: 'oldest', label: 'Oldest' },
                { value: 'status', label: 'Needs attention' },
                { value: 'name', label: 'Name A–Z' },
              ]}
              value={sort}
              onChange={(value) => setSort(value ?? 'newest')}
              w={160}
            />
          </Group>
          {results}
        </Stack>
      </Paper>
      <NewOrderModal
        opened={newOrderOpened}
        canHsat={canHsat}
        canRpm={canRpm}
        canReferral={canReferral}
        onSelect={(type) => {
          startNewOrder(type).catch(console.error);
        }}
        onClose={() => setNewOrderOpened(false)}
      />
      <NewPrescriptionModal
        opened={newOrderType === 'dme'}
        patient={patient}
        onClose={() => {
          setNewOrderType(undefined);
          setEditingDmeOrder(undefined);
        }}
        existingOrder={editingDmeOrder}
      />
      <ReferralModal
        opened={newOrderType === 'referral' && canReferral}
        patient={patient}
        onClose={() => setNewOrderType(undefined)}
        onCreated={(order) => {
          setOrders((current) => [...current, makeMedicationCard(order)]);
        }}
      />
      {orderEncounter && (
        <OrderNewTestModal
          opened={newOrderType === 'test'}
          onClose={() => {
            setNewOrderType(undefined);
            setEditingTestOrder(undefined);
          }}
          encounter={orderEncounter}
          patient={patient}
          practitioner={medplum.getProfile() as Practitioner | undefined}
          existingOrder={editingTestOrder}
        />
      )}
      <Modal opened={Boolean(cancelTarget)} onClose={() => setCancelTarget(undefined)} title="Cancel order" centered>
        <Stack>
          <Text>
            Cancel <b>{cancelTarget?.title}</b>? This action creates a cancellation task and cannot be undone.
          </Text>
          <Textarea
            label="Reason"
            required
            value={cancelReason}
            onChange={(event) => setCancelReason(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCancelTarget(undefined)}>
              Keep order
            </Button>
            <Button color="red" disabled={!cancelReason.trim()} onClick={() => cancelOrder().catch(console.error)}>
              Cancel order
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
